// /lib/chart-helpers/custom-overlays/specific-trade-panel/orderLineOverlay.ts

import { klinecharts } from "../../klinecharts-client";

export interface OrderLineData {
  price: number;
  orderType: "Buy Limit" | "Sell Limit" | "Buy Stop" | "Sell Stop";
  lots?: number;
  onPriceChange?: (orderType: string, price: number) => void;
}

// Function to get color based on order type (same colors as useOrderLines)
const getOrderLineColor = (orderType: string): string => {
  switch (orderType) {
    case "Buy Limit":
    case "Buy Stop":
      return "#2563EB";
    case "Sell Limit":
    case "Sell Stop":
      return "#DC2626";
    default:
      return "#6B7280";
  }
};

const getDraggedPrice = (event: any): number | null => {
  const pointValue = event?.overlay?.points?.[0]?.value;
  if (Number.isFinite(pointValue)) {
    return pointValue;
  }

  if (event?.yAxis && event?.y !== undefined) {
    const axisValue = event.yAxis.convertFromPixel(event.y);
    return Number.isFinite(axisValue) ? axisValue : null;
  }

  return null;
};

// Function to create visual elements for order lines
const createOrderLineFigures = (params: any) => {
  const { coordinates, bounding, styles, overlay, precision } = params;

  if (coordinates.length < 1) return [];

  const figures = [];
  const startPoint = coordinates[0];
  const orderData: OrderLineData = overlay.extendData;

  if (!orderData) return [];

  const lineColor = getOrderLineColor(orderData.orderType);

  // 1. Horizontal line across the chart - SOLID
  const lineStartX = 0;
  const lineEndX = bounding.width;

  const orderLine = {
    key: "orderLine",
    type: "line",
    attrs: {
      coordinates: [
        { x: lineStartX, y: startPoint.y },
        { x: lineEndX, y: startPoint.y },
      ],
    },
    styles: {
      style: "solid", // Solid line for order lines
      color: lineColor,
      size: 2,
    },
  };
  figures.push(orderLine);

  // 2. Badge with order type text - styled like trade line overlay
  const badgeText = orderData.orderType; // "Buy Limit", "Sell Stop", etc.
  const badgeWidth = badgeText.length * 8 + 12; // Estimated width
  const badgeX = bounding.width - badgeWidth - 10; // Position from right edge

  const badgeTextFigure = {
    key: "badgeText",
    type: "text",
    attrs: {
      x: badgeX,
      y: startPoint.y,
      text: badgeText,
      align: "left",
      baseline: "middle",
    },
    styles: {
      style: "fill",
      color: "#FFFFFF",
      size: 11,
      family: "Arial",
      weight: "bold",
      backgroundColor: lineColor,
      paddingLeft: 6,
      paddingRight: 6,
      paddingTop: 4,
      paddingBottom: 4,
      borderRadius: 4,
      borderStyle: "solid",
      borderColor: lineColor,
      borderSize: 1,
    },
  };
  figures.push(badgeTextFigure);

  return figures;
};

export const orderLineOverlay = {
  name: "customOrderLine",
  totalStep: 1,
  lock: false, // Allow dragging
  visible: true,
  zLevel: 8, // Above SL/TP but below trade lines
  needDefaultPointFigure: false,
  needDefaultXAxisFigure: false,
  needDefaultYAxisFigure: true, // Show price label on Y axis
  mode: "normal" as const,
  modeSensitivity: 8,

  createPointFigures: (params: any) => {
    return createOrderLineFigures(params);
  },

  // Price label on Y axis
  createYAxisFigures: (params: any) => {
    const { coordinates, overlay, yAxis } = params;

    if (coordinates.length < 1) return [];

    const orderData: OrderLineData = overlay.extendData;
    if (!orderData) return [];

    const lineColor = getOrderLineColor(orderData.orderType);
    const yCoord = coordinates[0].y;
    const currentPrice = yAxis.convertFromPixel(yCoord);
    const priceText = currentPrice.toFixed(5);

    return [
      {
        key: "yAxisOrderLabel",
        type: "text",
        attrs: {
          x: 0,
          y: yCoord,
          text: priceText,
          align: "left",
          baseline: "middle",
        },
        styles: {
          style: "fill",
          color: "#FFFFFF",
          size: 11,
          family: "Arial",
          weight: "bold",
          backgroundColor: lineColor,
          paddingLeft: 6,
          paddingRight: 6,
          paddingTop: 3,
          paddingBottom: 3,
          borderRadius: 3,
          borderStyle: "solid",
          borderColor: lineColor,
          borderSize: 1,
        },
      },
    ];
  },

  // Event handlers for dragging
  onClick: () => false,
  onDoubleClick: () => false,
  onPressedMoveStart: () => false, // Allow dragging to start
  onPressedMoving: (event: any) => {
    const { overlay } = event;
    const newPrice = getDraggedPrice(event);
    if (overlay?.extendData && newPrice !== null) {
      overlay.extendData.price = newPrice;
    }
    return false;
  },
  onPressedMoveEnd: (event: any) => {
    const { overlay } = event;
    const finalPrice = getDraggedPrice(event);
    if (overlay?.extendData && finalPrice !== null) {
      overlay.extendData.price = finalPrice;

      const onPriceChange =
        overlay.onPriceChange || overlay.extendData.onPriceChange;
      onPriceChange?.(overlay.extendData.orderType, finalPrice);
    }
    return false;
  },
};

// Function to register overlay
export const registerOrderLineOverlay = () => {
  try {
    klinecharts.registerOverlay(orderLineOverlay);
  } catch (error) {
    console.error("Failed to register order line overlay:", error);
  }
};

// Helper function to create order line overlay
export const createOrderLineOverlay = (
  chartInstance: any,
  orderData: OrderLineData,
  onPriceChange?: (orderType: string, price: number) => void
) => {
  if (!chartInstance) return null;

  // Get proper timestamp from chart data
  const dataList = chartInstance.getDataList();
  const timestamp =
    dataList && dataList.length > 0
      ? dataList[dataList.length - 1].timestamp
      : Date.now();

  const overlayConfig = {
    name: "customOrderLine",
    lock: false,
    points: [
      {
        timestamp: timestamp,
        value: orderData.price,
      },
    ],
    extendData: {
      ...orderData,
      onPriceChange,
    },
  };

  const overlayId = chartInstance.createOverlay(overlayConfig);

  if (overlayId && onPriceChange) {
    const overlay = chartInstance.getOverlayById(overlayId);
    if (overlay) {
      overlay.onPriceChange = onPriceChange;
    }
  }

  return overlayId;
};
